import React, { useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { useAuthStore } from "presentation/stores/use-auth-store";
import {
  useEquipmentsByFilter,
  useUpdateMyEquipmentAttributesMutation,
  useLogVehicleMileageMutation,
  useEquipmentAttributeDefinitions,
  useVehicleMileageLogs,
} from "presentation/hooks/use-query-equipments";
import { useEquipmentTypeByFilterAll } from "presentation/hooks/use-query-equipment-type";
import { Equipment } from "domain/entities/equipment";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import Spinner from "presentation/components/Shared/Spinner";
import EquipmentForm from "./EquipmentForm";
import {
  calculateEquipmentCompleteness,
  filterEquipmentsByVehicleType,
  getVehicleEquipmentType,
  parseEquipmentAttributes,
} from "./equipment-utils";
import { useIsMobile } from "presentation/mobile/hooks/useIsMobile";

interface MyEquipmentsTabProps {
  showOnlyVehicles?: boolean;
}

const MyEquipmentsTab: React.FC<MyEquipmentsTabProps> = ({
  showOnlyVehicles = false,
}) => {
  const toast = useRef<Toast>(null);
  const { userEmployee } = useAuthStore();
  const employeeId = userEmployee?.id; // ID do colaborador logado

  const isMobile = useIsMobile();

  const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(
    null,
  );
  const [attributesObject, setAttributesObject] = useState<any>({});
  const [isAttrDialogVisible, setIsAttrDialogVisible] = useState(false);

  const [isMileageDialogVisible, setIsMileageDialogVisible] = useState(false);
  const [mileageData, setMileageData] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    kms: 0,
  });

  // Buscar todos os tipos de equipamentos
  const { data: eqTypesResponse } = useEquipmentTypeByFilterAll({
    limit: 100,
  } as any);

  // Buscar equipamentos alocados ao funcionário logado
  const { data: response, isLoading } = useEquipmentsByFilter(
    {
      employeeId,
      limit: 100,
    },
    !!employeeId,
  );

  // Buscar definições de atributos para o equipamento selecionado para carregar o modal dinâmico
  const { data: allAttrDefsResponse } = useEquipmentAttributeDefinitions({});

  const updateAttributesMutation = useUpdateMyEquipmentAttributesMutation();
  const logMileageMutation = useLogVehicleMileageMutation();

  const handleOpenAttributes = (equipment: Equipment) => {
    setSelectedEquipment(equipment);
    setAttributesObject(parseEquipmentAttributes(equipment.attributes));
    setIsAttrDialogVisible(true);
  };

  const handleSaveAttributes = () => {
    if (!selectedEquipment?.id) return;
    updateAttributesMutation.mutate(
      {
        id: selectedEquipment.id,
        attributes: JSON.stringify(attributesObject),
      },
      {
        onSuccess: () => {
          toast.current?.show({
            severity: "success",
            summary: "Sucesso",
            detail: "Características guardadas.",
            life: 3000,
          });
          setIsAttrDialogVisible(false);
          setSelectedEquipment(null);
        },
        onError: (err: any) => {
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: err.message || "Erro ao guardar.",
            life: 3000,
          });
        },
      },
    );
  };

  const handleOpenMileage = (equipment: Equipment) => {
    setSelectedEquipment(equipment);
    setMileageData({
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      kms: 0,
    });
    setIsMileageDialogVisible(true);
  };

  const executeSaveMileage = () => {
    if (!selectedEquipment?.id) return;
    logMileageMutation.mutate(
      {
        id: selectedEquipment.id,
        month: mileageData.month,
        year: mileageData.year,
        kms: mileageData.kms,
      },
      {
        onSuccess: () => {
          toast.current?.show({
            severity: "success",
            summary: "Sucesso",
            detail: "KMs registado.",
            life: 3000,
          });
          setIsMileageDialogVisible(false);
          setSelectedEquipment(null);
        },
        onError: (err: any) => {
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: err.message || "Erro ao registar KMs.",
            life: 3000,
          });
        },
      },
    );
  };

  const handleSaveMileage = () => {
    if (!selectedEquipment?.id) return;

    const logs = mileageLogsData?.data || [];
    
    const previousLogs = logs.filter(log => log.year < mileageData.year || (log.year === mileageData.year && log.month < mileageData.month));
    if (previousLogs.length > 0) {
      previousLogs.sort((a, b) => b.year !== a.year ? b.year - a.year : b.month - a.month);
      const mostRecentPastLog = previousLogs[0];
      if (mileageData.kms < mostRecentPastLog.kms) {
        toast.current?.show({
          severity: "error",
          summary: "Valor Inválido",
          detail: `Os KMs não podem ser inferiores ao último registo (${mostRecentPastLog.kms} km em ${mostRecentPastLog.month}/${mostRecentPastLog.year}).`,
        });
        return;
      }
    }

    const futureLogs = logs.filter(log => log.year > mileageData.year || (log.year === mileageData.year && log.month > mileageData.month));
    if (futureLogs.length > 0) {
      futureLogs.sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month);
      const closestFutureLog = futureLogs[0];
      if (mileageData.kms > closestFutureLog.kms) {
        toast.current?.show({
          severity: "error",
          summary: "Valor Inválido",
          detail: `Os KMs não podem ser superiores ao registo seguinte (${closestFutureLog.kms} km em ${closestFutureLog.month}/${closestFutureLog.year}).`,
        });
        return;
      }
    }

    // Verificar se já existe log para o mesmo mês/ano
    const existingLog = mileageLogsData?.data?.find(
      (log) => log.month === mileageData.month && log.year === mileageData.year,
    );

    if (existingLog) {
      confirmDialog({
        message: `Já existe um registo de KMs de ${existingLog.kms.toLocaleString()} km para ${mileageData.month.toString().padStart(2, "0")}/${mileageData.year}. Deseja atualizar o valor para ${mileageData.kms.toLocaleString()} km?`,
        header: "Registo de KMs Existente",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Sim, Atualizar",
        rejectLabel: "Não",
        accept: () => executeSaveMileage(),
      });
    } else {
      executeSaveMileage();
    }
  };

  const statusLabelMap: Record<string, string> = {
    IN_USE: "Em uso",
    IN_MAINTENANCE: "Em manutenção",
    IN_STOCK: "Em stock",
    LOST: "Perdido",
    OBSOLETE: "Obsoleto",
  };

  const statusBodyTemplate = (rowData: Equipment) => {
    const status = rowData.status;
    const label = statusLabelMap[status] || status;
    let severity: "success" | "warning" | "danger" | "info" = "info";
    let customStyle: React.CSSProperties = {
      fontWeight: "600",
      fontSize: "0.85rem",
      padding: "0.25rem 0.6rem"
    };

    if (status === "IN_USE") {
      severity = "success";
      customStyle = { ...customStyle, backgroundColor: "#eafaf1", color: "#27ae60", border: "1px solid #c2f0d1" };
    } else if (status === "IN_MAINTENANCE") {
      severity = "warning";
      customStyle = { ...customStyle, backgroundColor: "#fff3e6", color: "#c26000", border: "1px solid #ffe0b2" };
    } else if (status === "IN_STOCK") {
      severity = "info";
      customStyle = { ...customStyle, backgroundColor: "#ebf5fe", color: "#1e40af", border: "1px solid #bfdbfe" };
    } else if (status === "LOST") {
      severity = "danger";
      customStyle = { ...customStyle, backgroundColor: "#fdf2f2", color: "#c81e1e", border: "1px solid #fde8e8" };
    } else if (status === "OBSOLETE") {
      severity = "info";
      customStyle = { ...customStyle, backgroundColor: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb" };
    }

    return <Tag value={label} severity={severity} rounded style={customStyle} />;
  };

  const equipmentTypeBodyTemplate = (rowData: Equipment) => {
    const eqType = eqTypesResponse?.data?.find(
      (t) => t.id === rowData.equipmentTypeId,
    );
    return eqType?.name || rowData.equipmentTypeId;
  };

  const completenessBodyTemplate = (rowData: Equipment) => {
    const pct = calculateEquipmentCompleteness(
      rowData,
      allAttrDefsResponse?.data?.data,
    );
    let color = "#22c55e"; // verde
    if (pct < 50) {
      color = "#ef4444"; // vermelho
    } else if (pct < 100) {
      color = "#f59e0b"; // amarelo
    }

    return (
      <div className="flex align-items-center gap-2">
        <div
          className="bg-gray-200 rounded-full"
          style={{
            backgroundColor: "#e5e7eb",
            borderRadius: "9999px",
            width: "4rem",
            height: "6px",
            overflow: "hidden",
          }}
        >
          <div
            style={{ width: `${pct}%`, backgroundColor: color, height: "100%" }}
          ></div>
        </div>
        <span style={{ fontWeight: "bold", color, fontSize: "0.85rem" }}>
          {pct}%
        </span>
      </div>
    );
  };

  const [isHistoryDialogVisible, setIsHistoryDialogVisible] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>("");
  const activeVehicleId = selectedEquipment?.id || selectedVehicleId;
  const { data: mileageLogsData, isLoading: isLoadingLogs } =
    useVehicleMileageLogs(
      activeVehicleId,
      (isHistoryDialogVisible || isMileageDialogVisible) && !!activeVehicleId,
    );

  const handleOpenHistory = (equipment: Equipment) => {
    if (!equipment.id) return;
    setSelectedVehicleId(equipment.id);
    setIsHistoryDialogVisible(true);
  };

  const actionBodyTemplate = (rowData: Equipment) => {
    const eqType = eqTypesResponse?.data?.find(
      (t) => t.id === rowData.equipmentTypeId,
    );
    const isVehicle = eqType?.name?.toLowerCase() === "viatura";
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-eye" severity="secondary"
          tooltip="Ver Equipamento"
          tooltipOptions={{ position: 'top' }}
          onClick={() => {
            setSelectedEquipment(rowData);
            setIsAttrDialogVisible(true);
          }}
          className="p-button-rounded p-button-text"
        />
        {isVehicle && (
          <>
            <Button
              icon="pi pi-car" severity="secondary"
              tooltip="Registar KMs"
              tooltipOptions={{ position: 'top' }}
              onClick={() => handleOpenMileage(rowData)}
              className="p-button-rounded p-button-text"
            />
            <Button
              icon="pi pi-history" severity="secondary"
              tooltip="Histórico de KMs"
              tooltipOptions={{ position: 'top' }}
              onClick={() => handleOpenHistory(rowData)}
              className="p-button-rounded p-button-text"
            />
          </>
        )}
      </div>
    );
  };

  if (isLoading) return <Spinner />;

  // Find the Viatura EquipmentType ID
  const viaturaType = getVehicleEquipmentType(eqTypesResponse?.data);

  const filteredData = filterEquipmentsByVehicleType(
    response?.data?.data,
    viaturaType?.id,
    showOnlyVehicles,
  );

  return (
    <div>
      <DataTable
        value={filteredData}
        emptyMessage={
          showOnlyVehicles
            ? "Nenhuma viatura alocada."
            : "Nenhum equipamento alocado."
        }
      >
        {!showOnlyVehicles && (
          <Column
            field="equipmentTypeId"
            header="Tipo de Equipamento"
            body={equipmentTypeBodyTemplate}
            sortable
          />
        )}
        <Column field="brand" header="Marca" sortable />
        <Column field="model" header="Modelo" sortable />
        <Column
          field="serialOrCarRegistration"
          header={showOnlyVehicles ? "Matrícula" : "Nº Série"}
          sortable
        />
        <Column
          field="status"
          header="Status"
          body={statusBodyTemplate}
          sortable
        />
        <Column header="Preenchimento" body={completenessBodyTemplate} />
        <Column
          header="Ações"
          body={actionBodyTemplate}
          style={{ width: "10rem" }}
        />
      </DataTable>

      <Toast ref={toast} />
      <ConfirmDialog />

      {/* Dialog para Atributos Dinâmicos */}
      <Dialog
        header="Detalhes e Características do Equipamento"
        visible={isAttrDialogVisible}
        style={{ width: "60vw" }}
        contentStyle={{ minHeight: "450px" }}
        onHide={() => setIsAttrDialogVisible(false)}
        maximized={isMobile}
        footer={
          <div>
            <Button
              label="Cancelar"
              icon="pi pi-times"
              onClick={() => setIsAttrDialogVisible(false)}
              className="p-button-text"
            />
            <Button
              label="Guardar"
              icon="pi pi-check"
              onClick={handleSaveAttributes}
              autoFocus
            />
          </div>
        }
      >
        <EquipmentForm
          selectedEquipment={selectedEquipment || ({} as Equipment)}
          setSelectedEquipment={setSelectedEquipment}
          attributesObject={attributesObject}
          setAttributesObject={setAttributesObject}
          isAdmin={false}
          readOnlyBaseFields={true}
          showOnlyVehicles={showOnlyVehicles}
        />
      </Dialog>

      {/* Dialog para KMs */}
      <Dialog
        header="Registar KMs Mensal"
        visible={isMileageDialogVisible}
        style={{ width: "30vw" }}
        onHide={() => setIsMileageDialogVisible(false)}
        footer={
          <div>
            <Button
              label="Cancelar"
              icon="pi pi-times"
              onClick={() => setIsMileageDialogVisible(false)}
              className="p-button-text"
            />
            <Button
              label="Registar"
              icon="pi pi-check"
              onClick={handleSaveMileage}
              autoFocus
            />
          </div>
        }
      >
        <div className="flex flex-column gap-4 mt-3">
          <div className="field">
            <span className="p-float-label">
              <Calendar
                id="mileageDate"
                value={new Date(mileageData.year, mileageData.month - 1, 1)}
                onChange={(e) => {
                  if (e.value instanceof Date) {
                    setMileageData({
                      ...mileageData,
                      month: e.value.getMonth() + 1,
                      year: e.value.getFullYear(),
                    });
                  }
                }}
                view="month"
                dateFormat="mm/yy"
                className="w-full"
                showIcon
              />
              <label htmlFor="mileageDate">Período (Mês/Ano)</label>
            </span>
          </div>

          <div className="field mb-5 mt-2">
            <span className="p-float-label">
              <InputText
                id="kms"
                type="number"
                value={mileageData.kms.toString()}
                onChange={(e) =>
                  setMileageData({
                    ...mileageData,
                    kms: Number(e.target.value),
                  })
                }
                className="w-full"
              />
              <label htmlFor="kms">KMs</label>
            </span>
          </div>
        </div>
      </Dialog>

      {/* Dialog para Histórico de KMs */}
      <Dialog
        header="Histórico de KMs da Viatura"
        visible={isHistoryDialogVisible}
        style={{ width: "35vw" }}
        onHide={() => {
          setIsHistoryDialogVisible(false);
          setSelectedVehicleId("");
        }}
        footer={
          <div>
            <Button
              label="Fechar"
              icon="pi pi-times"
              onClick={() => setIsHistoryDialogVisible(false)}
              className="p-button-text"
            />
          </div>
        }
      >
        <DataTable
          value={mileageLogsData?.data || []}
          loading={isLoadingLogs}
          emptyMessage="Sem registos de KMs para esta viatura."
          paginator
          rows={5}
          size="small"
        >
          <Column
            header="Período"
            body={(row) =>
              `${row.month.toString().padStart(2, "0")}/${row.year}`
            }
          />
          <Column
            header="KMs"
            body={(row) => `${row.kms.toLocaleString()} km`}
          />
          <Column
            header="Data do Registo"
            body={(row) => new Date(row.createdAt).toLocaleDateString("pt-PT")}
          />
        </DataTable>
      </Dialog>
    </div>
  );
};

export default MyEquipmentsTab;
